import { trackEvent, turnstileSiteKey } from "@koaform/analytics";
import { Turnstile, type TurnstileHandle } from "@koaform/ui";
import { useRef, useState } from "react";

import { api, ApiError } from "../lib/api.js";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

/**
 * Email-code sign in, plus Google.
 *
 * One component shared by the login screen and the publish dialog, so the two
 * entrances to an account cannot drift apart. Signing in also claims any
 * anonymous drafts this browser holds — the server does that on verify.
 */
export const AuthForm = ({
  onSignedIn,
  submitLabel = "Continue",
}: {
  onSignedIn: () => void | Promise<void>;
  submitLabel?: string;
}) => {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstile = useRef<TurnstileHandle>(null);

  const fail = (err: unknown) =>
    setError(
      err instanceof ApiError
        ? err.message
        : "Something went wrong. Try again.",
    );

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = await turnstile.current?.getToken();
      await api.requestCode(email, token ?? undefined);
      setStep("code");
    } catch (err) {
      fail(err);
      // Turnstile tokens are single-use; a retry needs a fresh one.
      turnstile.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.verifyCode(email, code);
      // claimedForms is only non-empty on a signup that adopted a draft, which
      // is the funnel step worth seeing: build-first then sign up.
      trackEvent("Signed in", { claimed: result.claimedForms.length });
      await onSignedIn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-[11px] border border-line-input p-[13px] text-[15px] text-ink focus:border-primary-600 focus:outline-none";
  const primary =
    "w-full rounded-[11px] bg-primary-600 p-3.5 text-[15px] font-bold text-white hover:bg-primary-700 disabled:bg-line-bold";

  return (
    <div>
      <a
        href={`${API_URL}/auth/google`}
        className="mb-2.5 block rounded-[11px] border border-line-bold bg-surface p-[13px] text-center text-[15px] font-semibold text-ink hover:border-primary-600 hover:bg-primary-50 hover:text-ink"
      >
        Continue with Google
      </a>

      <div className="mb-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-line-strong" />
        <span className="text-xs text-faint">or</span>
        <span className="h-px flex-1 bg-line-strong" />
      </div>

      {step === "email" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode();
          }}
        >
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@work.com"
            className={`${field} mb-2.5`}
          />
          <button type="submit" disabled={busy} className={primary}>
            {busy ? "Sending…" : "Continue with email"}
          </button>
        </form>
      ) : (
        <>
          <p className="m-0 mb-3 text-[14px] leading-[1.55] text-muted">
            We sent a 6-digit code to{" "}
            <strong className="text-ink">{email}</strong>.
          </p>
          {import.meta.env.DEV ? (
            <p className="m-0 mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] leading-[1.5] text-amber-800">
              Dev mode: no email is actually sent — the code is printed in the
              API terminal (and defaults to <strong>333333</strong>).
            </p>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verify();
            }}
          >
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="123456"
              className={`${field} mb-2.5 text-center font-mono text-[22px] tracking-[0.3em]`}
            />
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className={primary}
            >
              {busy ? "Checking…" : submitLabel}
            </button>
          </form>
          <button
            type="button"
            onClick={() => {
              setCode("");
              setError(null);
              setStep("email");
            }}
            className="mt-2.5 w-full text-[13px] text-muted hover:text-ink"
          >
            Use a different email
          </button>
        </>
      )}

      {error ? (
        <p role="alert" className="mt-3 mb-0 text-[13.5px] text-danger">
          {error}
        </p>
      ) : null}

      <Turnstile
        ref={turnstile}
        siteKey={turnstileSiteKey(import.meta.env.PROD)}
      />
    </div>
  );
};
